import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "setup/redux/RootReducer";
import { container } from "setup/dependency/inversify.config";
import { IClientPortalUsersService } from "services/ClientPortalUsersService";
import { ISessionService } from "services/SessionService";
import { IClientsService } from "services/ClientsService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { toast } from "react-toastify";
import { AddAccountFormSteps } from "./steps/StepModel";
import { GUID_EMPTY } from "global/constants";
import { IClientPortalAccountContactDetailsService } from "services/ClientPortalAccountContactDetailsService";
import { NewClientEmailModel } from "services/api/cplapi/CPLAPI";
import translations from "utils/translationUtility";
import { extractApiErrorMessage } from "utils/apiErrorUtility";
import { CPLError } from "cpl-react-common/Helpers/CustomErrors";

type AddAccountState = {
  CurrentStep: AddAccountFormSteps;
  PreviousStep: AddAccountFormSteps | null;
  Loading: boolean;
};

const initialState: AddAccountState = {
  CurrentStep: AddAccountFormSteps.ClientAccountNumber,
  PreviousStep: null,
  Loading: false,
};

export const linkClientAccount = createAsyncThunk<
  boolean,
  { clientAccountNumber: string },
  { state: RootState; rejectValue: string }
>("AddAccount/LinkClientAccount", async ({ clientAccountNumber }, thunkAPI) => {
  try {
    const usersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    const state = thunkAPI.getState();
    const accountLinked = await usersService.linkClientAccount(clientAccountNumber, state.auth.user!.emailAddress!);
    if (accountLinked) {
      const accountSettingsService = container.get<IClientPortalAccountContactDetailsService>(
        SERVICE_TYPES.ClientPortalAccountContactDetailsService
      );
      const newClientEmail = new NewClientEmailModel();
      newClientEmail.clientAccountNumber = clientAccountNumber;
      newClientEmail.emailAddress = state.auth.user!.emailAddress!;
      newClientEmail.description = "Linked email";
      newClientEmail.isDefault = true;
      newClientEmail.useForDocuments = true;
      newClientEmail.useForInvoices = true;

      accountSettingsService.createClientEmail(newClientEmail);
      const sessionService = container.get<ISessionService>(SERVICE_TYPES.SessionService);
      await sessionService.refreshCPLToken(true);
    }
    return accountLinked;
  } catch (err) {
    if (err instanceof CPLError) throw err;
    return thunkAPI.rejectWithValue(
      extractApiErrorMessage(err, `Cannot link ${translations.client.toLocaleLowerCase()} account`)
    );
  }
});

export const linkClientAccountWithEmail = createAsyncThunk<
  boolean,
  { clientAccountNumber: string; emailVerificationCode: string; emailAddress: string },
  { state: RootState; rejectValue: string }
>(
  "AddAccount/LinkClientAccountWithEmail",
  async ({ clientAccountNumber, emailAddress, emailVerificationCode }, thunkAPI) => {
    try {
      const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
      const state = thunkAPI.getState();
      await clientPortalUsersService.verifyEmail(
        emailAddress,
        emailVerificationCode,
        state.companyProvider.companyId ?? ""
      );
      const accountLinked = await clientPortalUsersService.linkClientAccount(clientAccountNumber, emailAddress);
      if (accountLinked) {
        const accountSettingsService = container.get<IClientPortalAccountContactDetailsService>(
          SERVICE_TYPES.ClientPortalAccountContactDetailsService
        );
        const newClientEmail = new NewClientEmailModel();
        newClientEmail.clientAccountNumber = clientAccountNumber;
        newClientEmail.emailAddress = state.auth.user!.emailAddress!;
        newClientEmail.description = "Linked email";
        newClientEmail.isDefault = true;
        newClientEmail.useForDocuments = true;
        newClientEmail.useForInvoices = true;

        accountSettingsService.createClientEmail(newClientEmail);
        const sessionService = container.get<ISessionService>(SERVICE_TYPES.SessionService);
        await sessionService.refreshCPLToken(true);
      }
      return accountLinked;
    } catch (err) {
      if (err instanceof CPLError) throw err;
      return thunkAPI.rejectWithValue(
        extractApiErrorMessage(err, `Cannot verify ${translations.client.toLocaleLowerCase()} email`)
      );
    }
  }
);

export const sendClientEmailVerificationCode = createAsyncThunk<
  boolean,
  { clientAccountNumber: string; emailAddress: string },
  { state: RootState; rejectValue: string }
>("AddAccount/SendClientEmailVerificationCode", async ({ clientAccountNumber, emailAddress }, thunkAPI) => {
  try {
    const clientPortalUsersService = container.get<IClientPortalUsersService>(SERVICE_TYPES.ClientPortalUsersService);
    const clientsService = container.get<IClientsService>(SERVICE_TYPES.ClientsService);
    const state = thunkAPI.getState();
    const clientId = await clientsService.getEmailMatchesClient(clientAccountNumber, emailAddress);

    const emailFound = clientId !== GUID_EMPTY;

    if (emailFound) {
      await clientPortalUsersService.sendEmailVerificationCode(emailAddress, state.companyProvider.companyId ?? "");
    }

    return emailFound;
  } catch (err) {
    if (err instanceof CPLError) throw err;
    return thunkAPI.rejectWithValue(extractApiErrorMessage(err, "Cannot send email verification code"));
  }
});

export const addAccountSlice = createSlice({
  name: "addAccount",
  initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      state.CurrentStep = action.payload;

      if (action.payload == AddAccountFormSteps.EmailAddress) {
        state.PreviousStep = state.PreviousStep = AddAccountFormSteps.ClientAccountNumber;
      } else if (action.payload == AddAccountFormSteps.EmailVerificationCode) {
        state.PreviousStep = state.PreviousStep = AddAccountFormSteps.EmailAddress;
      } else {
        state.PreviousStep = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(linkClientAccount.fulfilled, (state, action) => {
        if (action.payload === true) {
          state.CurrentStep = AddAccountFormSteps.Success;
        } else {
          state.CurrentStep = AddAccountFormSteps.EmailAddress;
          state.PreviousStep = AddAccountFormSteps.ClientAccountNumber;
        }

        state.Loading = false;
      })
      .addCase(linkClientAccount.pending, (state) => {
        state.Loading = true;
      })
      .addCase(linkClientAccount.rejected, (state, action) => {
        state.CurrentStep = AddAccountFormSteps.ClientAccountNumber;
        state.PreviousStep = null;
        state.Loading = false;

        const error =
          (action.payload as string) ??
          action.error.message ??
          `Cannot link ${translations.client.toLocaleLowerCase()} account`;

        toast.error(error);
      })
      .addCase(sendClientEmailVerificationCode.fulfilled, (state, action) => {
        if (action.payload === true) {
          state.CurrentStep = AddAccountFormSteps.EmailVerificationCode;
          state.PreviousStep = AddAccountFormSteps.EmailAddress;
        } else {
          toast.error(`${translations.client} account with this email doesn't exist`);
        }

        state.Loading = false;
      })
      .addCase(sendClientEmailVerificationCode.pending, (state) => {
        state.Loading = true;
      })
      .addCase(sendClientEmailVerificationCode.rejected, (state, action) => {
        state.CurrentStep = AddAccountFormSteps.EmailAddress;
        state.PreviousStep = AddAccountFormSteps.ClientAccountNumber;
        state.Loading = false;

        const error = action.payload ?? action.error.message ?? "Cannot send email verification code";

        toast.error(error);
      })
      .addCase(linkClientAccountWithEmail.fulfilled, (state, action) => {
        if (action.payload === true) {
          state.CurrentStep = AddAccountFormSteps.Success;
        } else {
          state.CurrentStep = AddAccountFormSteps.ClientAccountNumber;
          state.PreviousStep = null;
          toast.error(`Cannot link ${translations.client.toLocaleLowerCase()} account`);
        }

        state.Loading = false;
      })
      .addCase(linkClientAccountWithEmail.pending, (state) => {
        state.Loading = true;
      })
      .addCase(linkClientAccountWithEmail.rejected, (state, action) => {
        state.CurrentStep = AddAccountFormSteps.ClientAccountNumber;
        state.PreviousStep = null;
        state.Loading = false;

        const error =
          action.payload ?? action.error.message ?? `Cannot verify ${translations.client.toLocaleLowerCase()} email`;

        toast.error(error);
      });
  },
});

export const { setCurrentStep } = addAccountSlice.actions;

export const selectCurrentStep = (state: RootState) => {
  return state.addAccount.CurrentStep;
};
export const selectPreviousStep = (state: RootState) => {
  return state.addAccount.PreviousStep;
};
export const selectLoading = (state: RootState) => {
  return state.addAccount.Loading;
};

export default addAccountSlice.reducer;
